/**
 * Central permission utility for APFv2.
 * All role/permission checks in the frontend should use these helpers.
 * DO NOT use `user.roleId === 'rN'` for access control — use these instead.
 */

export interface User {
  roleId: string;
  roleScope?: string;
  tenantId?: string;
  permissions?: string[];
  [key: string]: any;
}

/**
 * Returns true if the user has the given permission string.
 * Super Admin (r1) always returns true.
 */
export const hasPermission = (user: User | null | undefined, permission: string): boolean => {
  if (!user) return false;
  if (user.roleId === 'r1') return true; // Super Admin bypass
  return Array.isArray(user.permissions) && user.permissions.includes(permission);
};

/**
 * Returns true if the user has ANY permission for the given module.
 * e.g. hasAnyModulePermission(user, 'dashboard') checks for 'dashboard:*'
 */
export const hasAnyModulePermission = (user: User | null | undefined, moduleName: string): boolean => {
  if (!user) return false;
  if (user.roleId === 'r1') return true;
  if (!Array.isArray(user.permissions)) return false;
  return user.permissions.some(p => p.startsWith(`${moduleName}:`));
};

/**
 * Known platform-only role IDs.
 */
const PLATFORM_ROLE_IDS = new Set(['r1', 'r2', 'r3', 'r5', 'r8', 'r9', 'r10']);

/**
 * Returns true if the user's role is scoped to a Tenant.
 */
export const isTenantScoped = (user: User | null | undefined): boolean => {
  if (!user) return false;
  if (user.roleId === 'r4' || user.roleId === 'r7') return true;
  if (user.roleScope && user.roleScope !== 'Platform') return user.roleScope === 'Tenant';
  if (user.tenantId && !PLATFORM_ROLE_IDS.has(user.roleId)) return true;
  return false;
};

/**
 * Returns true if the user is a Platform-level user.
 */
export const isPlatformScoped = (user: User | null | undefined): boolean => {
  return !isTenantScoped(user);
};
